import React from 'react';
import { ColumnWithGap } from '../../../../PlusWebsiteEditor/sections/HeroBlock/HeroBlock.styled';
import { ConditionEditorProps } from './interface';
import ConditionSelectorEditor from '../../../../FormEditors/ConditionSelectorEditor';
import QuestionnaireSelectorEditor from '../../../../FormEditors/QuestionnaireSelectorEditor';
import ReturningQuestionnaireSelectorEditor from '../../../../FormEditors/ReturningQuestionnaireSelectorEditor';
import NumberEditor from '../../../../FormEditors/NumberEditor';
import TextEditor from '../../../../FormEditors/TextEditor';
import ClinicConditionPromoEditor from './ClinicConditionPromoEditor';
import SwitchEditor from '../../../../FormEditors/SwitchEditor';
import ClinicMasterProductsSelectorEditor from '../../../../FormEditors/ClinicMasterProductSelectorEditor';
import styled from 'styled-components';
import OrderSelectorEditor from '../../../../FormEditors/OrderSelectorEditor';
import SelectEditor from '../../../../FormEditors/SelectEditor';
import ConditionSpotlightEditor from './ConditionSpotlightEditor';
import useClinicById from '../../hooks/useClinicById';
import { useDisplayOrderOfConditions } from './useDisplayOrderOfConditions';

const PRESCRIBING_PROTOCOL_HELP =
  'The policy document opened by the "Prescribing Protocol" link on a Connect order for this condition. It applies whenever the prescriber who claims the order has no protocol of their own for the condition. Set those on the clinic\'s Prescribers tab. Must be a publicly reachable https:// link; leave empty to show no protocol at all.';

const GENDER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
];

interface ConditionEditorBodyProps extends Pick<ConditionEditorProps, 'form' | 'index' | 'clinicId'> {}

function ConditionEditorBody({ form, index, clinicId }: ConditionEditorBodyProps) {
  const condition = form.watch(`clinic_conditions.${index}`);
  const isFromAnotherClinic = Boolean(condition?.clinic_id && condition.clinic_id !== clinicId);
  const productsClinicId = isFromAnotherClinic && condition?.clinic_id ? condition.clinic_id : clinicId;

  const { maxOrderNumber } = useDisplayOrderOfConditions({ form, index });

  // Spotlight has its own endpoint, so read it from server truth (not the form) to prefill + gate on saved.
  const { clinic } = useClinicById(clinicId);
  const serverCondition = clinic?.clinic_conditions?.find((c) => c._id === condition?._id);
  const isSavedCondition = Boolean(serverCondition);

  return (
    <>
      {isFromAnotherClinic && (
        <ImportNotice>
          <strong>This condition is from another clinic.</strong> Fields cannot be modified.
        </ImportNotice>
      )}
      <DisabledFieldset $isDisabled={isFromAnotherClinic}>
        <ColumnWithGap>
          <ConditionSelectorEditor name={`clinic_conditions.${index}.condition_id`} control={form.control} />
          <QuestionnaireSelectorEditor name={`clinic_conditions.${index}.questionnaire_id`} control={form.control} />
          <ReturningQuestionnaireSelectorEditor name={`clinic_conditions.${index}.returning_questionnaire_id`} control={form.control} />
          <NumberEditor
            name={`clinic_conditions.${index}.returning_order_history_period_in_days`}
            control={form.control}
            label="Returning patient order history in days"
          />
          <OrderSelectorEditor
            name={`clinic_conditions.${index}.order.default` as any}
            control={form.control}
            label={`Display order (1-${maxOrderNumber})`}
            maxNumber={maxOrderNumber}
          />
          <ClinicMasterProductsSelectorEditor
            clinicId={productsClinicId}
            name={`clinic_conditions.${index}.master_products`}
            control={form.control}
          />
          <SwitchEditor name={`clinic_conditions.${index}.is_video_call_required`} control={form.control} label="Video consultation" />
          <SelectEditor
            name={`clinic_conditions.${index}.gender`}
            control={form.control}
            label="Gender (For Her / For Him filter)"
            options={GENDER_OPTIONS}
            disabled={isFromAnotherClinic}
          />
          <TextEditor
            name={`clinic_conditions.${index}.prescribing_protocol_link`}
            control={form.control}
            label="Default prescribing protocol URL (https://)"
            tooltip={PRESCRIBING_PROTOCOL_HELP}
            optional
            disabled={isFromAnotherClinic}
          />
          <ClinicConditionPromoEditor form={form} index={index} isDisabled={isFromAnotherClinic} />
          {condition?._id && (
            <ConditionSpotlightEditor
              key={serverCondition?.spotlight?._id || 'new'}
              clinicId={clinicId}
              clinicConditionId={condition._id}
              spotlight={serverCondition?.spotlight || null}
              isSavedCondition={isSavedCondition}
              isDisabled={isFromAnotherClinic}
            />
          )}
        </ColumnWithGap>
      </DisabledFieldset>
    </>
  );
}

export default ConditionEditorBody;

const DisabledFieldset = styled.fieldset<{ $isDisabled: boolean }>`
  border: none;
  padding: 0;
  margin: 0;
  opacity: ${props => props.$isDisabled ? 0.7 : 1};
  pointer-events: ${props => props.$isDisabled ? 'none' : 'auto'};
`;

const ImportNotice = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  background-color: #fff9c4;
  border-left: 4px solid #fbc02d;
  color: #424242;
  border-radius: 4px;
  font-size: 16px;
  line-height: 1.5;
`;
